import { useEffect, useRef } from "react";
import { logger } from "@/utils/logger";
import type { CreditModalContext } from "@/components/create/creditModalContext";
import { CreatePageState } from "@/stores/createPageStore";

interface UseCreditManagementProps {
  state: CreatePageState;
  session: any;
  roles: { admin?: boolean };
  isReturningFromCreditPurchase?: boolean;
  creditsLoading: boolean;
  creditsError: any;
  creditsFetching?: boolean;
  creditBalance: number | null;
  hasSufficientCredits: (amount: number) => boolean | null;
  showCreditModal: (context?: NonNullable<CreditModalContext>) => void;
  setError: (error: string | null) => void;
}

/**
 * Hook to manage credit checking on step 1.
 *
 * Simplified version - credit purchase redirect handling is now done
 * by useCreditPurchaseGate at the page level, which blocks the page
 * until credits are refreshed.
 *
 * This hook only handles:
 * - Auto-checking credits on step 1 before generation
 */
export function useCreditManagement({
  state,
  session,
  roles,
  isReturningFromCreditPurchase = false,
  creditsLoading,
  creditsError,
  creditsFetching = false,
  creditBalance,
  hasSufficientCredits,
}: UseCreditManagementProps) {
  // Track if we've already checked credits to prevent duplicate checks
  const hasCheckedCreditsRef = useRef(false);

  // Track last signature to prevent duplicate Strict Mode runs
  const creditCheckSignatureRef = useRef<string | null>(null);

  // Credit Checking: Check balance on step 1 before any generation
  useEffect(() => {
    const hasFrontImage =
      !!state.generation.images.front.url ||
      !!state.generation.images.front.path;

    // Create signature to detect actual changes (avoid Strict Mode double-runs)
    const signature = [
      session?.user?.id || "",
      roles.admin ? "admin" : "user",
      state.form.currentStep,
      hasFrontImage ? "has-image" : "no-image",
      creditsLoading ? "loading" : "ready",
      creditsFetching ? "fetching" : "stable",
      creditBalance ?? "null",
      state.ui.modals.credit.dismissed ? "dismissed" : "active",
      state.ui.modals.credit.show ? "shown" : "hidden",
    ].join("|");

    if (creditCheckSignatureRef.current === signature) {
      return;
    }
    creditCheckSignatureRef.current = signature;

    // Skip conditions
    if (!session || roles.admin) {
      hasCheckedCreditsRef.current = false;
      return;
    }

    if (state.form.currentStep !== 1) {
      hasCheckedCreditsRef.current = false;
      return;
    }

    if (hasFrontImage) {
      hasCheckedCreditsRef.current = false;
      return;
    }

    if (isReturningFromCreditPurchase || creditsLoading || creditsFetching) {
      return;
    }

    if (creditsError) {
      return;
    }

    if (state.generation.progress.loading) {
      return;
    }

    if (state.ui.modals.credit.dismissed || state.ui.modals.credit.show) {
      return;
    }

    if (hasCheckedCreditsRef.current) {
      return;
    }

    // Check credits
    const creditCheck = hasSufficientCredits(1);

    if (creditCheck === null) {
      // Still loading
      return;
    }

    hasCheckedCreditsRef.current = true;

    if (creditCheck === false) {
      // Do NOT proactively pop the credit modal on step-1 entry. Ambushing a
      // brand-new (0-credit) user with a purchase modal before they have
      // designed anything drove them out of the funnel. The modal is still
      // shown at the moment of a real generation attempt (see
      // useGenerationActions), which is the intent-aligned time to ask.
      logger.debug(
        "Auto-check: insufficient credits (modal deferred to attempt)",
        {
          component: "useCreditManagement",
          creditBalance,
        }
      );
    }
  }, [
    session,
    roles.admin,
    isReturningFromCreditPurchase,
    state.form.currentStep,
    state.generation.images.front.url,
    state.generation.images.front.path,
    creditsLoading,
    creditsError,
    creditsFetching,
    state.generation.progress.loading,
    state.ui.modals.credit.dismissed,
    state.ui.modals.credit.show,
    hasSufficientCredits,
    creditBalance,
  ]);
}
